import React, { useState } from 'react';
import { generateGradient } from './gradientGenerator';
import { apiClient } from '../../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_CARD,
} from '../legacy-ball/table-management/designTokens';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

/** Resolve a posterUrl — prepend API base for relative paths */
function resolvePosterUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_BASE}${url}`;
}

interface AgendaItem {
  id: string;
  title: string;
  startTime: string;
  endTime: string;
  speaker?: string | null;
  description?: string | null;
  details?: string | null;
}

export interface EventCardProps {
  event: {
    id: string;
    name: string;
    date: string;
    startTime: string;
    endTime?: string;
    description?: string | null;
    posterUrl?: string | null;
    specialMessage?: string | null;
    programUrl?: string | null;
    mealInfo?: string | null;
    transportInfo?: string | null;
    venueName?: string | null;
    venueAddress?: string | null;
    parkingInfo?: string | null;
    parkingImageUrl?: string | null;
    dressCode?: string | null;
    contactName?: string | null;
    contactPhone?: string | null;
    vendorCount?: number;
  };
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  const [year, month, day] = datePart.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTime(timeStr: string): string {
  if (!timeStr) return '';
  let hours: number, minutes: number;
  if (timeStr.includes('T')) {
    const timePart = timeStr.split('T')[1].slice(0, 5);
    [hours, minutes] = timePart.split(':').map(Number);
  } else if (timeStr.includes(':')) {
    [hours, minutes] = timeStr.split(':').map(Number);
  } else {
    return timeStr;
  }
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
}

export const EventCard: React.FC<EventCardProps> = ({ event }) => {
  const [imageError, setImageError] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [rsvpStatus, setRsvpStatus] = useState<'attending' | 'not_attending' | null>(null);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [agendaItems, setAgendaItems] = useState<AgendaItem[]>([]);
  const [agendaLoading, setAgendaLoading] = useState(false);
  const [showAgenda, setShowAgenda] = useState(false);
  const [expandedAgenda, setExpandedAgenda] = useState<Set<string>>(new Set());
  const [showMore, setShowMore] = useState(false);
  const [vendors, setVendors] = useState<{ id: string; businessName: string; category: string | null; contactPerson: string | null }[]>([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [showVendors, setShowVendors] = useState(false);

  const showGradient = !event.posterUrl || imageError;
  const resolvedPoster = resolvePosterUrl(event.posterUrl);
  const gradient = generateGradient(event.name);
  const formattedDate = formatDate(event.date);
  const formattedTime = formatTime(event.startTime);
  const formattedEndTime = event.endTime ? formatTime(event.endTime) : null;

  const directionsUrl =
    event.venueAddress || event.venueName
      ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
          [event.venueAddress, event.venueName].filter(Boolean).join(', ')
        )}`
      : null;

  const handleRsvp = async (status: 'attending' | 'not_attending') => {
    setRsvpLoading(true);
    try {
      await apiClient(`/events/${event.id}/event-rsvp`, {
        method: 'POST',
        body: { status },
      });
      setRsvpStatus(status);
    } catch {
      // Silently fail
    } finally {
      setRsvpLoading(false);
    }
  };

  const handleShowAgenda = async () => {
    if (showAgenda) {
      setShowAgenda(false);
      return;
    }
    if (agendaItems.length === 0) {
      setAgendaLoading(true);
      try {
        const items = await apiClient<AgendaItem[]>(`/events/${event.id}/agenda`);
        setAgendaItems(items);
      } catch {
        setAgendaItems([]);
      } finally {
        setAgendaLoading(false);
      }
    }
    setShowAgenda(true);
  };

  // ── Collapsed: compact horizontal card ──────────────────
  if (!expanded) {
    return (
      <article
        style={styles.card}
        aria-label={`${event.name}, ${formattedDate}`}
        onClick={() => setExpanded(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded(true);
          }
        }}
      >
        <div style={styles.thumbSmall}>
          {showGradient ? (
            <div style={{ ...styles.gradientSmall, background: gradient }} aria-hidden="true" />
          ) : (
            <img src={resolvedPoster!} alt="" style={styles.imgSmall} onError={() => setImageError(true)} />
          )}
        </div>
        <div style={styles.summaryDetails}>
          <h3 style={styles.eventName}>{event.name}</h3>
          <span style={styles.detailText}>{formattedDate}</span>
          <span style={styles.detailText}>
            {formattedTime}{formattedEndTime && ` – ${formattedEndTime}`}
          </span>
          {event.venueName && <span style={styles.venueText}>{event.venueName}</span>}
        </div>
      </article>
    );
  }

  // ── Expanded: full detail view with large rounded image ──
  return (
    <article
      style={styles.expandedCard}
      aria-label={`${event.name} details`}
    >
      {/* Close button */}
      <button
        style={styles.closeBtn}
        onClick={() => setExpanded(false)}
        aria-label="Close event details"
      >
        ✕
      </button>

      {/* Large rounded poster/gradient */}
      <div style={styles.heroContainer}>
        {showGradient ? (
          <div style={{ ...styles.heroGradient, background: gradient }} aria-hidden="true" />
        ) : (
          <img
            src={resolvePosterUrl(event.posterUrl)!}
            alt={event.name}
            style={styles.heroImage}
            onError={() => setImageError(true)}
          />
        )}
      </div>

      {/* Scrollable details below */}
      <div style={styles.detailScroll}>
        <h2 style={styles.expandedName}>{event.name}</h2>

        <div style={styles.infoRow}>
          <span style={styles.infoIcon}>📅</span>
          <span style={styles.infoText}>{formattedDate}</span>
        </div>
        <div style={styles.infoRow}>
          <span style={styles.infoIcon}>🕐</span>
          <span style={styles.infoText}>
            {formattedTime}{formattedEndTime && ` – ${formattedEndTime}`}
          </span>
        </div>
        {event.venueName && (
          <div style={styles.infoRow}>
            <span style={styles.infoIcon}>🏛️</span>
            <span style={styles.infoText}>{event.venueName}</span>
          </div>
        )}
        {event.venueAddress && (
          <div style={styles.infoRow}>
            <span style={styles.infoIcon}>📍</span>
            <span style={styles.infoText}>{event.venueAddress}</span>
          </div>
        )}

        {event.description && (
          <p style={styles.description}>{event.description}</p>
        )}

        {/* Directions */}
        {directionsUrl && (
          <a
            href={directionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.directionsLink}
            aria-label={`Get directions to ${event.venueName || 'venue'}`}
          >
            🧭 Get Directions
          </a>
        )}

        {/* Parking Info */}
        {(event.parkingInfo || event.parkingImageUrl) && (
          <div style={styles.infoBlock}>
            <span style={styles.infoBlockLabel}>🅿️ Parking</span>
            {event.parkingInfo && <span style={styles.infoBlockText}>{event.parkingInfo}</span>}
            {event.parkingImageUrl && (
              <img src={event.parkingImageUrl} alt="Parking map" style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: '8px', marginTop: '6px', border: '1px solid #e0e0e0' }} />
            )}
          </div>
        )}

        {/* Dress Code */}
        {event.dressCode && (
          <div style={styles.infoBlock}>
            <span style={styles.infoBlockLabel}>👔 Dress Code</span>
            <span style={styles.infoBlockText}>{event.dressCode}</span>
          </div>
        )}

        {/* More Info toggle (MCs, meal, transport, contact) */}
        {(event.mealInfo || event.transportInfo || event.contactName) && (
          <>
            <button
              style={styles.moreButton}
              onClick={() => setShowMore((prev) => !prev)}
              aria-label={showMore ? 'Hide additional info' : 'Show additional info'}
            >
              ⓘ {showMore ? 'Less Info' : 'More Info'}
            </button>

            {showMore && (
              <div style={styles.moreSection}>
                {event.mealInfo && (
                  <div style={styles.infoBlock}>
                    <span style={styles.infoBlockLabel}>🍽️ Meal Info</span>
                    <span style={styles.infoBlockText}>{event.mealInfo}</span>
                  </div>
                )}
                {event.transportInfo && (
                  <div style={styles.infoBlock}>
                    <span style={styles.infoBlockLabel}>🚌 Transport</span>
                    <span style={styles.infoBlockText}>{event.transportInfo}</span>
                  </div>
                )}
                {event.contactName && (
                  <div style={styles.infoBlock}>
                    <span style={styles.infoBlockLabel}>👤 Contact</span>
                    <span style={styles.infoBlockText}>
                      {event.contactName}
                      {event.contactPhone && ` • ${event.contactPhone}`}
                    </span>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Special Message Banner */}
        {event.specialMessage && (
          <div style={{
            backgroundColor: '#fffbeb',
            border: '1px solid #f59e0b',
            borderRadius: '8px',
            padding: '10px 14px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            fontSize: '0.88rem',
            color: '#92400e',
            lineHeight: '1.4',
            marginTop: '12px',
          }}>
            <span aria-hidden="true">📢</span>
            <span dangerouslySetInnerHTML={{ __html: event.specialMessage }} />
          </div>
        )}

        {/* View Program Button */}
        {event.programUrl && (
          <a
            href={event.programUrl.startsWith('/') ? `${API_BASE}${event.programUrl}` : event.programUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              marginTop: '10px',
              padding: '8px 14px',
              backgroundColor: '#f0f7f2',
              border: '1px solid #c8e6c9',
              borderRadius: '6px',
              fontSize: '0.85rem',
              fontWeight: 600,
              color: '#1a6b3c',
              textDecoration: 'none',
            }}
          >
            📄 View Program
          </a>
        )}

        {/* RSVP */}
        <div style={styles.rsvpRow}>
          <button
            style={{ ...styles.rsvpButton, ...(rsvpStatus === 'attending' ? styles.rsvpActive : {}) }}
            onClick={() => handleRsvp('attending')}
            disabled={rsvpLoading}
            aria-label="RSVP Attending"
          >
            ✅ Attending
          </button>
          <button
            style={{ ...styles.rsvpButton, ...styles.rsvpDecline, ...(rsvpStatus === 'not_attending' ? styles.rsvpDeclineActive : {}) }}
            onClick={() => handleRsvp('not_attending')}
            disabled={rsvpLoading}
            aria-label="RSVP Can't make it"
          >
            ✕ Can't make it
          </button>
        </div>

        {/* Agenda */}
        <button
          style={styles.agendaButton}
          onClick={handleShowAgenda}
          aria-label={`${showAgenda ? 'Hide' : 'View'} agenda for ${event.name}`}
        >
          📋 {showAgenda ? 'Hide Agenda' : 'View Agenda'}
        </button>

        {/* Quick info pills: meal, transport, vendors */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
          {event.mealInfo && (
            <span style={{ fontSize: '0.75rem', backgroundColor: 'rgba(0,0,0,0.05)', padding: '3px 8px', borderRadius: '12px', color: '#374151' }}>🍽️ Meal</span>
          )}
          {event.transportInfo && (
            <span style={{ fontSize: '0.75rem', backgroundColor: 'rgba(0,0,0,0.05)', padding: '3px 8px', borderRadius: '12px', color: '#374151' }}>🚌 Shuttle</span>
          )}
          {event.vendorCount !== undefined && event.vendorCount > 0 && (
            <span style={{ fontSize: '0.75rem', backgroundColor: 'rgba(0,0,0,0.05)', padding: '3px 8px', borderRadius: '12px', color: '#374151' }}>🏪 {event.vendorCount} Vendor{event.vendorCount !== 1 ? 's' : ''}</span>
          )}
        </div>

        {agendaLoading && <span style={styles.agendaLoadingText}>Loading agenda...</span>}

        {showAgenda && !agendaLoading && agendaItems.length > 0 && (
          <div style={styles.agendaList}>
            <div style={{ fontSize: '0.85rem', fontWeight: 800, color: '#0f2d1f', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: '0.6rem' }}>
              {event.name} AGENDA
            </div>
            {agendaItems.map((item, idx) => (
              <div key={item.id} style={{ display: 'flex', alignItems: 'stretch', borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'hidden', backgroundColor: '#fff', marginBottom: '0.5rem' }}>
                {/* Order number */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '44px', backgroundColor: '#f0fdf4', color: '#1a6b3c', fontSize: '1.25rem', fontWeight: 800, flexShrink: 0 }}>
                  {idx + 1}
                </div>
                {/* Vertical separator bar */}
                <div style={{ width: '3px', backgroundColor: '#1a6b3c', flexShrink: 0 }} />
                {/* Content */}
                <div style={{ padding: '0.55rem 0.75rem', flex: 1 }}>
                  <div style={styles.agendaTitle}>{item.title}</div>
                  {item.speaker && <div style={styles.agendaSpeaker}>{item.speaker}</div>}
                  {item.description && <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '3px', lineHeight: 1.3 }}>{item.description}</div>}
                  {item.details && item.details.trim() && (
                    <div style={{ marginTop: '5px' }}>
                      <button
                        type="button"
                        onClick={() => setExpandedAgenda((prev) => { const n = new Set(prev); n.has(item.id) ? n.delete(item.id) : n.add(item.id); return n; })}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '0.75rem', fontWeight: 600, color: '#1a6b3c' }}
                        aria-expanded={expandedAgenda.has(item.id)}
                        aria-label={expandedAgenda.has(item.id) ? `Hide details for ${item.title}` : `Show more info for ${item.title}`}
                      >
                        {expandedAgenda.has(item.id) ? '▲ Hide info' : 'ℹ️ More info'}
                      </button>
                      {expandedAgenda.has(item.id) && (
                        <div
                          style={{ marginTop: '5px', padding: '0.6rem 0.75rem', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '0.8rem', color: '#374151', lineHeight: 1.5 }}
                          dangerouslySetInnerHTML={{ __html: item.details }}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {showAgenda && !agendaLoading && agendaItems.length === 0 && (
          <span style={styles.agendaEmptyText}>No agenda items yet.</span>
        )}
      </div>
    </article>
  );
};

const styles: Record<string, React.CSSProperties> = {
  // ── Collapsed card (compact) ──
  card: {
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: BG_CARD,
    borderRadius: '12px',
    border: `1px solid ${BORDER_LIGHT}`,
    overflow: 'hidden',
    marginBottom: '12px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    cursor: 'pointer',
  },
  thumbSmall: {
    width: '30%',
    minHeight: '100px',
    flexShrink: 0,
  },
  imgSmall: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
    display: 'block',
  },
  gradientSmall: {
    width: '100%',
    height: '100%',
    minHeight: '100px',
  },
  summaryDetails: {
    display: 'flex',
    flexDirection: 'column',
    padding: '10px 12px',
    gap: '3px',
    flex: 1,
    justifyContent: 'center',
  },
  eventName: {
    margin: 0,
    fontSize: '0.95rem',
    fontWeight: 600,
    color: TEXT_DARK,
    lineHeight: '1.3',
  },
  detailText: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    lineHeight: '1.3',
  },
  venueText: {
    fontSize: '0.8rem',
    color: PRIMARY,
    fontWeight: 500,
    lineHeight: '1.3',
  },

  // ── Expanded detail view ──
  expandedCard: {
    position: 'relative' as const,
    backgroundColor: BG_CARD,
    borderRadius: '16px',
    border: `1px solid ${BORDER_LIGHT}`,
    overflow: 'hidden',
    marginBottom: '12px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
  },
  closeBtn: {
    position: 'absolute' as const,
    top: '12px',
    right: '12px',
    zIndex: 10,
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0,0,0,0.5)',
    color: '#fff',
    border: 'none',
    fontSize: '1rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  },
  heroContainer: {
    width: '100%',
    height: '200px',
    borderRadius: '16px 16px 0 0',
    overflow: 'hidden',
  },
  heroImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
    display: 'block',
  },
  heroGradient: {
    width: '100%',
    height: '100%',
  },
  detailScroll: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    maxHeight: '50vh',
    overflowY: 'auto' as const,
  },
  expandedName: {
    margin: 0,
    fontSize: '1.2rem',
    fontWeight: 700,
    color: TEXT_DARK,
    lineHeight: '1.3',
  },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  infoIcon: {
    fontSize: '1rem',
    flexShrink: 0,
  },
  infoText: {
    fontSize: '0.9rem',
    color: TEXT_DARK,
    lineHeight: '1.4',
  },
  description: {
    margin: 0,
    fontSize: '0.88rem',
    color: TEXT_DARK,
    lineHeight: '1.5',
    paddingTop: '4px',
    borderTop: `1px solid ${BORDER_LIGHT}`,
  },
  directionsLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.9rem',
    color: '#1565c0',
    fontWeight: 500,
    textDecoration: 'none',
    padding: '8px 0',
  },
  infoBlock: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
  },
  infoBlockLabel: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: TEXT_MUTED,
  },
  infoBlockText: {
    fontSize: '0.88rem',
    color: TEXT_DARK,
    lineHeight: '1.4',
    whiteSpace: 'pre-wrap' as const,
  },
  moreButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: 'none',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '8px 14px',
    fontSize: '0.88rem',
    color: PRIMARY,
    fontWeight: 500,
    cursor: 'pointer',
  },
  moreSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px',
    padding: '10px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    border: `1px solid ${BORDER_LIGHT}`,
  },
  rsvpRow: {
    display: 'flex',
    gap: '8px',
  },
  rsvpButton: {
    flex: 1,
    padding: '10px 12px',
    fontSize: '0.85rem',
    fontWeight: 500,
    border: `1px solid ${PRIMARY}`,
    borderRadius: '8px',
    backgroundColor: 'transparent',
    color: PRIMARY,
    cursor: 'pointer',
  },
  rsvpActive: {
    backgroundColor: PRIMARY,
    color: '#fff',
  },
  rsvpDecline: {
    borderColor: '#d32f2f',
    color: '#d32f2f',
  },
  rsvpDeclineActive: {
    backgroundColor: '#d32f2f',
    color: '#fff',
  },
  agendaButton: {
    background: 'none',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '0.9rem',
    color: PRIMARY,
    fontWeight: 500,
    cursor: 'pointer',
    textAlign: 'left' as const,
  },
  agendaLoadingText: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
  },
  agendaList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    paddingLeft: '4px',
  },
  agendaItem: {
    borderLeft: `3px solid ${PRIMARY}`,
    paddingLeft: '10px',
  },
  agendaTime: {
    fontSize: '0.75rem',
    color: TEXT_MUTED,
    fontWeight: 500,
  },
  agendaTitle: {
    fontSize: '0.88rem',
    color: TEXT_DARK,
    fontWeight: 500,
  },
  agendaSpeaker: {
    fontSize: '0.75rem',
    color: TEXT_MUTED,
    fontStyle: 'italic' as const,
  },
  agendaEmptyText: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    fontStyle: 'italic' as const,
  },
};

export default EventCard;
